/**
 * Strongest first. `single_match` and `ambiguous` are co-motion matches (a tight fit to one AIS
 * fix); `probable` and `possible` come from the single-fix cone and carry odds; `likely` is sea
 * travel with no vessel identified -- the stored key predates the cone tiers, hence its label.
 */
export const ENTITY_VESSEL_EVIDENCE_TYPES = ['single_match', 'ambiguous', 'probable', 'possible', 'likely'] as const;
export type EntityVesselEvidenceType = (typeof ENTITY_VESSEL_EVIDENCE_TYPES)[number];

export const ENTITY_VESSEL_EVIDENCE_LABELS: Record<EntityVesselEvidenceType, string> = {
  single_match: 'Single vessel match',
  ambiguous: 'Multiple candidate vessels',
  probable: 'Probable vessel',
  possible: 'Possible vessel',
  likely: 'Sea travel, no vessel'
};

/** Evidence that names a vessel worth acting on; `possible` is a lead, `likely` names none. */
export const ENTITY_VESSEL_IDENTIFIED_TYPES: readonly EntityVesselEvidenceType[] = ['single_match', 'ambiguous', 'probable'];

export const isEntityVesselConeEvidence = (evidenceType: EntityVesselEvidenceType) => evidenceType === 'probable' || evidenceType === 'possible';

/** Odds as a whole percentage, e.g. `74%`. */
export const formatEntityVesselOdds = (odds: number | null | undefined) => (odds == null ? '' : `${Math.round(odds * 100)}%`);

/**
 * What to show for a cone match: its measured accuracy (`~64%`) when it has one, else its raw odds
 * marked as such (`8% odds`, for low-ranked alternatives). Raw odds overstate certainty.
 */
export const formatEntityVesselConfidence = (candidate: { accuracy?: number | null; odds: number | null } | null | undefined) => {
  if (candidate?.accuracy != null) return `~${Math.round(candidate.accuracy * 100)}%`;
  if (candidate?.odds != null) return `${Math.round(candidate.odds * 100)}% odds`;
  return '';
};

export type EntityVesselSource = 'BB' | 'QQ' | 'CC';

export interface EntityVesselSearchRequest {
  advertiser_ids: string[];
  start_at: string;
  end_at: string;
  evidence_types?: EntityVesselEvidenceType[];
  sources?: EntityVesselSource[];
}

export interface EntityVesselSummary {
  advertiser_id: string;
  journey_count: number;
  single_match_count: number;
  ambiguous_count: number;
  probable_count: number;
  possible_count: number;
  likely_count: number;
}

/** Voyages whose vessel is identified at probable or better. */
export const identifiedVesselCount = (summary: EntityVesselSummary) => summary.single_match_count + summary.ambiguous_count + (summary.probable_count ?? 0);

/** e.g. "5 ship voyages in selected period: 2 single vessel match, 1 probable vessel, 2 sea travel, no vessel". */
export const describeEntityVesselSummary = (summary: EntityVesselSummary) => {
  const counts: [number, string][] = [
    [summary.single_match_count, ENTITY_VESSEL_EVIDENCE_LABELS.single_match],
    [summary.ambiguous_count, ENTITY_VESSEL_EVIDENCE_LABELS.ambiguous],
    [summary.probable_count ?? 0, ENTITY_VESSEL_EVIDENCE_LABELS.probable],
    [summary.possible_count ?? 0, ENTITY_VESSEL_EVIDENCE_LABELS.possible],
    [summary.likely_count, ENTITY_VESSEL_EVIDENCE_LABELS.likely]
  ];
  const parts = counts.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label.toLowerCase()}`);
  const total = `${summary.journey_count} ship voyage${summary.journey_count === 1 ? '' : 's'} in selected period`;
  return parts.length ? `${total}: ${parts.join(', ')}` : total;
};

export interface EntityVesselLocation {
  latitude: number | null;
  longitude: number | null;
  name: string | null;
  kind: string | null;
  country: string | null;
  device_distance_km: number | null;
  military_port: string | null;
}

export interface EntityVesselCandidate {
  rank: number;
  /** Rank 1: `confirmed` (co-motion), `probable` or `possible` (cone). Ranks 2+: `alternative`. */
  status: 'confirmed' | 'probable' | 'possible' | 'alternative';
  /** `co_motion` or `cone`; null for journeys published before the cone existed. */
  method: 'co_motion' | 'cone' | null;
  mmsi: string;
  imo: string | null;
  name: string | null;
  call_sign: string | null;
  vessel_type: string | null;
  length_m: number | null;
  beam_m: number | null;
  fix_at: string;
  fix_latitude: number | null;
  fix_longitude: number | null;
  sog_kt: number | null;
  course_deg: number | null;
  nav_status: string | null;
  aoi_name: string | null;
  supporting_pings: number | null;
  distance_km: number | null;
  time_offset_minutes: number | null;
  speed_ratio: number | null;
  course_diff_deg: number | null;
  /** Cone only: the model's odds that the device was aboard this vessel rather than another or none. */
  odds: number | null;
  /** Cone only, at or above the possible cutoff: how often matches with these odds were right when tested. */
  accuracy?: number | null;
  /** Cone only: how many other vessels' cones also held the device. */
  competitors: number | null;
  /** Cone only: where the vessel should have been at the device's best-fitting ping. */
  projected_latitude: number | null;
  projected_longitude: number | null;
  /**
   * Cone only: the cone the model scored against, as closed rings of `[longitude, latitude]` --
   * several when clipped to water leaves separate pieces.
   */
  cone: [number, number][][];
  military_vessel: boolean | null;
  military_vessel_signals: string[];
}

/** A matched vessel's marker on the map, as hovered: the vessel and how it was matched. */
export interface EntityVesselMatchHover {
  candidate: EntityVesselCandidate;
  evidence_type: EntityVesselEvidenceType;
}

export interface EntityVesselJourney {
  event_date: string;
  source: EntityVesselSource;
  advertiser_id: string;
  journey_id: string;
  run_id: string;
  evidence_type: EntityVesselEvidenceType;
  ambiguity_type: 'unique' | 'same_route' | 'divergent' | 'unmatched';
  candidate_count: number;
  device_departure_at: string;
  device_arrival_at: string;
  device_departure: { latitude: number; longitude: number };
  device_arrival: { latitude: number; longitude: number };
  route_origin?: EntityVesselLocation;
  route_destination?: EntityVesselLocation;
  distance_km: number;
  speed_kmh: number;
  sea_departure_at: string;
  sea_arrival_at: string;
  sea_ping_count: number;
  sea_path_km: number;
  sea_displacement_km: number;
  sea_speed_kt: number;
  beyond_territorial_share: number;
  /** The voyage's sea pings as `[longitude, latitude]` pairs, thinned to at most 60 points. */
  sea_track: [number, number][];
  ferry_route_name: string | null;
  ferry_route_operator: string | null;
  ferry_route_share: number;
  crossing_share: number;
  water_share: number;
  identity_conflict: boolean;
  primary_candidate: EntityVesselCandidate | null;
  alternatives: EntityVesselCandidate[];
}

export interface EntityVesselJourneyResponse {
  advertiser_id: string;
  start_at: string;
  end_at: string;
  journeys: EntityVesselJourney[];
  pagination: { limit: number; next_cursor: string | null };
}

export interface EntityVesselJourneyRequest {
  advertiser_id: string;
  start_at: string;
  end_at: string;
  evidence_types?: EntityVesselEvidenceType[];
  sources?: EntityVesselSource[];
  limit?: number;
  cursor?: string;
}

export interface EntityVesselRunStatus {
  run_id: string;
  input_date: string;
  sources: EntityVesselSource[];
  algorithm_version: string;
  published_at: string;
  match_row_count: number;
  journey_count: number;
}

export interface EntityVesselStatusResponse {
  available: boolean;
  coverage: { start_at: string; end_at: string } | null;
  sources: EntityVesselSource[];
  published_run_count: number;
  latest_run: EntityVesselRunStatus | null;
}

export const getEntityVesselJourneyId = (journey: EntityVesselJourney) => [journey.source, journey.run_id, journey.event_date, journey.journey_id].join(':');

export type EntityVesselRouteCoordinates = [[number, number], [number, number]];

/** A voyage's straight line, from the device's own departure to its arrival. Ports are context. */
export const getEntityVesselRouteCoordinates = (journey: EntityVesselJourney): EntityVesselRouteCoordinates => [
  [journey.device_departure.longitude, journey.device_departure.latitude],
  [journey.device_arrival.longitude, journey.device_arrival.latitude]
];

/**
 * The path the device actually took at sea, ping by ping.
 *
 * The straight endpoint line hides the shape of a voyage: a device crossing the Singapore Strait
 * drew a line to its arrival ashore while its pings ran west along the coast, which made the
 * matched vessel look far from a route it was near. Falls back to the endpoints for a journey
 * published before tracks were stored.
 */
export const getEntityVesselTrack = (journey: EntityVesselJourney): [number, number][] =>
  journey.sea_track?.length >= 2 ? journey.sea_track : getEntityVesselRouteCoordinates(journey);
